import { useRef, useState } from 'react'
import { MATRIX_LABELS } from '@crm/shared'
import { AssignSelectedControl } from './AssignSelectedControl'

type Props = {
  selectedCount: number
  isDeleting: boolean
  isApplyingMatrix: boolean
  matrixError: string | null
  onDelete: () => void
  onApplyMatrix: (matrix: string | null) => void
  onCancel: () => void
  // Назначение выбранных позиций закупщику или РОЗ (видимость в очереди).
  // null — «показать всем» (снять назначение). Список — AssignSelectedControl.
  onAssign?: (assignedToId: string | null) => void
  isAssigning?: boolean
  // Кнопки Excel-импорта/экспорта менеджерских квот.
  onDownloadExcel?: () => void
  onUploadExcel?: (file: File) => void
  isDownloadingExcel?: boolean
  isUploadingExcel?: boolean
  excelMessage?: string | null
  // «Отправить брокеру»: позиции уходят логисту в «Нетарифку» (17.10.2026).
  onSendToLogist?: () => void
  isSendingToLogist?: boolean
}

/**
 * Панель действий над выделенными позициями. Сверху — «Удалить»,
 * рядом — дропдаун выбора матрицы + кнопка «Применить» (массово
 * ставит выбранную матрицу всем выделенным позициям). «Снять матрицу»
 * = значение `__none__` в select (backend получит null).
 */
export function SelectedItemsBar({
  selectedCount,
  isDeleting,
  isApplyingMatrix,
  matrixError,
  onDelete,
  onApplyMatrix,
  onCancel,
  onAssign,
  isAssigning = false,
  onDownloadExcel,
  onUploadExcel,
  isDownloadingExcel = false,
  isUploadingExcel = false,
  excelMessage = null,
  onSendToLogist,
  isSendingToLogist = false,
}: Props) {
  const [matrixValue, setMatrixValue] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleApply = () => {
    if (!matrixValue || isApplyingMatrix) return
    const payload = matrixValue === '__none__' ? null : matrixValue
    onApplyMatrix(payload)
  }

  return (
    <div className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-2 bg-gray-50/60 flex-wrap">
      <span className="text-xs text-gray-500 mr-1">Выбрано: {selectedCount}</span>

      {/* Массовая установка матрицы */}
      <div className="flex items-center gap-1.5">
        <select
          value={matrixValue}
          onChange={(e) => setMatrixValue(e.target.value)}
          disabled={isApplyingMatrix}
          className="px-2 py-1 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
          aria-label="Матрица для выделенных позиций"
        >
          <option value="" disabled>
            Матрица…
          </option>
          {MATRIX_LABELS.map((label) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
          <option value="__none__">— Снять матрицу —</option>
        </select>
        <button
          onClick={handleApply}
          disabled={!matrixValue || isApplyingMatrix}
          className="px-3 py-1.5 text-xs bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
        >
          {isApplyingMatrix ? 'Применение…' : 'Применить'}
        </button>
      </div>

      {/* Назначить выбранные позиции закупщику (видимость в очереди квот).
          Только для матриц Китай/Производители — бэк пропускает остальные. */}
      {onAssign && (
        <>
          <div className="w-px h-4 bg-gray-200 mx-1" aria-hidden />
          <AssignSelectedControl
            placeholder="Показать закупщику…"
            isAssigning={isAssigning}
            onAssign={onAssign}
          />
        </>
      )}

      {/* Логист сам собирает из присланных позиций запрос брокеру. */}
      {onSendToLogist && (
        <>
          <div className="w-px h-4 bg-gray-200 mx-1" aria-hidden />
          <button
            onClick={onSendToLogist}
            disabled={isSendingToLogist}
            className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
            title="Отправить выбранные позиции логисту на нетарифку: он соберёт запрос брокеру"
          >
            {isSendingToLogist ? 'Отправка…' : 'Отправить брокеру'}
          </button>
        </>
      )}

      <div className="w-px h-4 bg-gray-200 mx-1" aria-hidden />

      <button
        onClick={onDelete}
        disabled={isDeleting}
        className="px-3 py-1.5 text-xs bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
      >
        {isDeleting ? 'Удаление...' : 'Удалить'}
      </button>

      <button
        onClick={onCancel}
        className="px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-100"
      >
        Отменить
      </button>

      {/* Excel-инструменты для быстрого ввода квот менеджером.
          Шаблон скачивается с partNumber/brand/qty; менеджер заполняет
          поставщика+цену+заметку для матрицы Стоки и/или РФ; при загрузке
          бэк фильтрует полные дубли (itemId+supplier+price+note). */}
      {(onDownloadExcel || onUploadExcel) && (
        <>
          <div className="w-px h-4 bg-gray-200 mx-1" aria-hidden />
          {onDownloadExcel && (
            <button
              onClick={onDownloadExcel}
              disabled={isDownloadingExcel}
              className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              title="Скачать Excel с выделенными позициями для быстрого ввода квот"
            >
              {isDownloadingExcel ? '...' : '⬇ Скачать Excel'}
            </button>
          )}
          {onUploadExcel && (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingExcel}
                className="px-3 py-1.5 text-xs bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 disabled:opacity-50"
                title="Загрузить заполненный Excel — создадим квоты по строкам"
              >
                {isUploadingExcel ? 'Загрузка…' : '⬆ Загрузить Excel'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) onUploadExcel(file)
                  e.target.value = ''
                }}
              />
            </>
          )}
        </>
      )}

      {matrixError && <span className="text-xs text-red-600 basis-full mt-1">{matrixError}</span>}
      {excelMessage && (
        <span className="text-xs text-gray-700 basis-full mt-1">{excelMessage}</span>
      )}
    </div>
  )
}
